import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/services/grants')({
  component: GrantsPage,
  head: () => ({
    meta: [
      { title: 'Grants finder — Warren-Blackwood Electorate Guide' },
      {
        name: 'description',
        content:
          'Curated list of federal and WA government grants open to Warren-Blackwood residents, small businesses, farmers and community groups — agriculture, business, community infrastructure, environment, arts and sport.',
      },
      { property: 'og:title', content: 'Grants finder — Warren-Blackwood' },
      {
        property: 'og:description',
        content:
          'Federal and WA grants for farmers, businesses, community groups, environment, arts and sport in the south-west.',
      },
    ],
  }),
});

interface Grant {
  /** Grant or program name. */
  name: string;
  /** One-sentence plain-English purpose. */
  purpose: string;
  /** Eligible applicants — keep short. */
  who: string;
  /** Indicative funding range. */
  amount: string;
  /** Open/closing pattern — annual round, rolling, etc. */
  deadline: string;
  /** Official application or info link. */
  url: string;
}

interface Category {
  id: string;
  title: string;
  blurb: string;
  grants: Grant[];
}

const CATEGORIES: Category[] = [
  {
    id: 'agriculture',
    title: 'Agriculture & primary producers',
    blurb:
      'Drought, climate-smart farming, biosecurity, on-farm productivity and value-add food businesses.',
    grants: [
      {
        name: 'Future Drought Fund — Farm Business Resilience Program (WA)',
        purpose: 'Subsidised planning and training for farm businesses to manage climate, financial and operational risk.',
        who: 'WA primary producers and farm businesses.',
        amount: 'Co-contribution training — heavily subsidised, often under $500 per participant.',
        deadline: 'Rolling intakes through delivery partners.',
        url: 'https://www.agric.wa.gov.au/climate-change/farm-business-resilience-program',
      },
      {
        name: 'Climate-Smart Agriculture Program',
        purpose: 'On-farm practice change projects that improve soil, water, biodiversity and emissions outcomes.',
        who: 'Producers, NRM groups, industry bodies; delivered through partners including South West Catchments Council.',
        amount: '$5,000 – $250,000+ depending on stream.',
        deadline: 'Multiple rounds; check current call.',
        url: 'https://www.dcceew.gov.au/agriculture-land/farm-food-drought/climate-smart-agriculture',
      },
      {
        name: 'AgriFutures Producer Grants',
        purpose: 'Small grants for producers to test on-farm innovations and emerging industries.',
        who: 'Individual producers and small producer groups.',
        amount: 'Up to $25,000.',
        deadline: 'Annual round, usually mid-year.',
        url: 'https://agrifutures.com.au/',
      },
      {
        name: 'DPIRD Boosting Biosecurity Defences grants',
        purpose: 'Industry-led biosecurity preparedness and surveillance projects.',
        who: 'Industry groups, grower associations, RBGs.',
        amount: 'Varies by stream.',
        deadline: 'Annual EOI process.',
        url: 'https://www.agric.wa.gov.au/biosecurity-quarantine',
      },
      {
        name: 'Carbon Farming Outreach Program',
        purpose: 'Free education and advice to help farmers and land managers understand carbon farming opportunities.',
        who: 'WA farmers and pastoralists.',
        amount: 'Free — not a cash grant; capacity building.',
        deadline: 'Rolling.',
        url: 'https://www.dcceew.gov.au/climate-change/emissions-reduction/carbon-farming-outreach-program',
      },
      {
        name: 'Rural Financial Counselling Service WA',
        purpose: 'Free financial counselling for primary producers and small related businesses in financial difficulty (not a grant but unlocks access to support).',
        who: 'Primary producers, fishers, related small businesses.',
        amount: 'Free.',
        deadline: 'Rolling.',
        url: 'https://www.rfcswa.com.au/',
      },
    ],
  },
  {
    id: 'small-business',
    title: 'Small business & industry',
    blurb:
      'Start-up, growth, exports, digital uplift and tourism investment — federal and WA programs.',
    grants: [
      {
        name: 'Industry Growth Program',
        purpose: 'Advisory and matched grants for innovative small businesses commercialising new products in priority sectors (incl. agriculture, renewables, manufacturing).',
        who: 'SMEs with innovative products in National Reconstruction Fund priority areas.',
        amount: 'Early stage $50k–$250k; commercialisation up to $5m (matched).',
        deadline: 'Rolling.',
        url: 'https://business.gov.au/grants-and-programs/industry-growth-program',
      },
      {
        name: 'Export Market Development Grants (EMDG)',
        purpose: 'Reimburses eligible export marketing expenses for SMEs developing overseas markets.',
        who: 'Australian SMEs exporting (or ready to export) goods, services and IP.',
        amount: 'Tiered: up to $80k/year (Tier 3, established exporters).',
        deadline: 'Annual application window via Austrade.',
        url: 'https://www.austrade.gov.au/australian/export/export-grants',
      },
      {
        name: 'WA Tourism Industry Growth Fund',
        purpose: 'Co-funded grants for tourism operators investing in new or upgraded visitor experiences.',
        who: 'WA tourism businesses and not-for-profits.',
        amount: 'Varies by round; typically $50k–$5m co-contribution.',
        deadline: 'Round-based.',
        url: 'https://www.tourism.wa.gov.au/funding-and-grants',
      },
      {
        name: 'Regional Economic Development (RED) Grants — SWDC',
        purpose: 'Co-funded projects that deliver economic benefit to the south-west region.',
        who: 'Local government, not-for-profits, business groups and Aboriginal corporations across the south-west.',
        amount: '$50,000 – $300,000.',
        deadline: 'Annual round, usually opens early in calendar year.',
        url: 'https://www.swdc.wa.gov.au/funding-opportunities/red-grants/',
      },
    ],
  },
  {
    id: 'community',
    title: 'Community infrastructure & local projects',
    blurb:
      'Council and not-for-profit infrastructure, halls, facility upgrades and community-led projects.',
    grants: [
      {
        name: 'Growing Regions Program',
        purpose: 'Federal capital grants for community infrastructure in regional and rural areas (successor to Building Better Regions Fund).',
        who: 'Local governments and not-for-profit incorporated bodies.',
        amount: '$500,000 – $15 million.',
        deadline: 'Round-based.',
        url: 'https://www.infrastructure.gov.au/territories-regions-cities/regional-australia/growing-regions-program',
      },
      {
        name: 'Stronger Communities Programme',
        purpose: 'Small federal grants for local capital projects — equipment, minor works, community facility upgrades.',
        who: 'Incorporated not-for-profits and local councils, nominated through the federal MP.',
        amount: '$2,500–$20,000. Councils must match funding 50%; other groups can be funded up to 100%.',
        deadline: 'Annual round.',
        url: 'https://business.gov.au/grants-and-programs/stronger-communities-programme',
      },
      {
        name: 'Volunteer Grants',
        purpose: 'Small federal grants for not-for-profits to support volunteers — equipment, training, fuel reimbursement.',
        who: 'Community organisations engaging volunteers.',
        amount: '$1,000 – $5,000.',
        deadline: 'Annual round.',
        url: 'https://www.dss.gov.au/grants/programs-services/volunteer-grants',
      },
      {
        name: 'Lotterywest grants',
        purpose: 'WA\'s largest community grants source — covers community projects, capital works, organisational capacity, crisis relief and more.',
        who: 'WA not-for-profits, local government and Aboriginal corporations.',
        amount: '$5,000 to multi-million.',
        deadline: 'Rolling — most streams accept applications any time.',
        url: 'https://www.lotterywest.wa.gov.au/grants',
      },
      {
        name: 'CBH Group Grass Roots Community Grants',
        purpose: 'Funding for community projects in WA grain-growing regions.',
        who: 'Community groups in CBH grain catchment shires (incl. parts of Warren-Blackwood).',
        amount: 'Up to $10,000 (events) or $20,000 (small infrastructure)',
        deadline: 'Two rounds a year (February and August)',
        url: 'https://www.cbh.com.au/about-cbh/community',
      },
    ],
  },
  {
    id: 'environment',
    title: 'Environment, NRM & Landcare',
    blurb:
      'Catchment management, native vegetation, biodiversity, threatened species and on-ground Landcare.',
    grants: [
      {
        name: 'Saving Native Species Program',
        purpose: 'Recovery actions for threatened species and ecological communities — relevant for Western Ringtail Possum, Black Cockatoos, Numbat habitat in the region.',
        who: 'NRM groups, researchers, Indigenous land managers, not-for-profits.',
        amount: 'Round-dependent.',
        deadline: 'Round-based.',
        url: 'https://www.dcceew.gov.au/environment/biodiversity/threatened/recovery-funding',
      },
      {
        name: 'South West Catchments Council (SWCC) sub-grants',
        purpose: 'On-ground NRM project funding delivered regionally — fencing, revegetation, weed control, citizen science.',
        who: 'Landholders, Landcare groups, community organisations in the south-west.',
        amount: 'Varies — small project to multi-year.',
        deadline: 'Round-based per program.',
        url: 'https://www.swccnrm.org.au/',
      },
      {
        name: 'State NRM Program (WA)',
        purpose: 'WA Government grants for natural resource management projects.',
        who: 'NRM groups, local government, community groups, Aboriginal organisations.',
        amount: '$10,000 – $500,000+.',
        deadline: 'Annual round.',
        url: 'https://www.wa.gov.au/service/environment/environment-information-services/state-nrm-program',
      },
      {
        name: 'Communities Environment Program',
        purpose: 'Small federal grants for local environmental projects, nominated through the federal MP.',
        who: 'Community groups and not-for-profits.',
        amount: '$2,500 – $20,000.',
        deadline: 'Annual round when funded.',
        url: 'https://www.dcceew.gov.au/environment/biodiversity/conservation',
      },
    ],
  },
  {
    id: 'arts',
    title: 'Arts & culture',
    blurb:
      'Individual artists, regional arts organisations, festivals, exhibitions, and cultural infrastructure.',
    grants: [
      {
        name: 'Country Arts WA — Regional Arts Fund',
        purpose: 'Project, touring, professional development and quick-response funding for regional WA artists and arts organisations.',
        who: 'Individual artists, arts groups and organisations in regional WA.',
        amount: '$2,000 – $30,000 depending on stream.',
        deadline: 'Multiple rounds + quick-response stream.',
        url: 'https://countryartswa.asn.au/grants/',
      },
      {
        name: 'Creative Australia grants',
        purpose: 'Federal arts funding across individuals, projects and organisations.',
        who: 'Artists, arts workers and arts organisations.',
        amount: '$5,000 – several hundred thousand.',
        deadline: 'Multiple programs, varying deadlines.',
        url: 'https://creative.gov.au/investment-and-development/',
      },
      {
        name: 'Department of Creative Industries, Tourism and Sport — Arts grants',
        purpose: 'WA Government arts funding for organisations, projects and individuals.',
        who: 'WA artists and arts organisations.',
        amount: 'Varies by program.',
        deadline: 'Multiple rounds across the year.',
        url: 'https://www.cits.wa.gov.au/',
      },
      {
        name: 'Healthway — Arts &amp; Health sponsorships',
        purpose: 'Sponsorship for arts events that promote health messages.',
        who: 'WA arts organisations and event delivery partners.',
        amount: '$5,000 – $200,000.',
        deadline: 'Two rounds per year.',
        url: 'https://www.healthway.wa.gov.au/',
      },
    ],
  },
  {
    id: 'sport',
    title: 'Sport, recreation & youth',
    blurb:
      'Club equipment, facility upgrades, junior participation and athlete development.',
    grants: [
      {
        name: 'KidSport (WA)',
        purpose: 'Vouchers covering club fees for children aged 5–18 from eligible WA families.',
        who: 'Families on eligible concessions; clubs register to accept vouchers.',
        amount: 'Up to $300 per child per financial year',
        deadline: 'Rolling.',
        url: 'https://www.cits.wa.gov.au/',
      },
      {
        name: 'Community Sporting and Recreation Facilities Fund (CSRFF)',
        purpose: 'WA Government capital grants for local sport and recreation infrastructure.',
        who: 'Local governments, sporting clubs and incorporated community groups.',
        amount: 'Small grants $2,500–$200,000 (up to 50% of project cost, two rounds a year); Forward Planning grants $166,667–$2.5 million (up to one-third, one round a year)',
        deadline: 'Two rounds per year via local council.',
        url: 'https://www.cits.wa.gov.au/',
      },
      {
        name: 'Local Sporting Champions',
        purpose: 'Travel-cost grants for young athletes competing at state or national level.',
        who: 'Athletes, coaches and officials aged 13–18.',
        amount: '$500, plus travel-distance supplements, up to $750',
        deadline: 'Three rounds per year.',
        url: 'https://www.sportaus.gov.au/grants_and_funding/local_sporting_champions',
      },
      {
        name: 'Play Our Way',
        purpose: 'Federal grants improving facilities and participation opportunities for women and girls in sport.',
        who: 'Sporting organisations, councils, not-for-profits.',
        amount: '$20,000 – $5m.',
        deadline: 'Round-based.',
        url: 'https://www.sportaus.gov.au/grants_and_funding',
      },
    ],
  },
];

function GrantsPage() {
  const h2: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.45rem',
    color: 'var(--rivergum)',
    marginTop: 'var(--sp-xl)',
    marginBottom: 'var(--sp-sm)',
    borderBottom: '1px solid var(--light-border)',
    paddingBottom: '0.3rem',
    letterSpacing: '0.02em',
    scrollMarginTop: '1rem',
  };
  const p: React.CSSProperties = {
    color: 'var(--ink)',
    lineHeight: 1.65,
    marginBottom: 'var(--sp-sm)',
  };
  const link: React.CSSProperties = { color: 'var(--green-mid)', fontWeight: 600 };
  const callout = (bg: string, border: string): React.CSSProperties => ({
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: 8,
    padding: 'var(--sp-md)',
    marginBottom: 'var(--sp-md)',
  });

  const grantCard: React.CSSProperties = {
    background: '#fff',
    border: '1px solid var(--rule)',
    borderRadius: 8,
    padding: '0.85rem 1rem',
    display: 'grid',
    gap: '0.45rem',
  };
  const fieldLabel: React.CSSProperties = {
    fontSize: 11,
    color: 'var(--ink-mid)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontWeight: 700,
  };

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 820,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <p
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--green-mid)',
          margin: 0,
          marginBottom: '0.35rem',
        }}
      >
        Services · Grants finder
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 5vw, 2.75rem)',
          color: 'var(--green)',
          margin: 0,
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.1,
        }}
      >
        Grants finder — Warren-Blackwood
      </h1>
      <p
        style={{
          color: 'var(--ink-mid)',
          fontStyle: 'italic',
          fontSize: '1.05rem',
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.5,
        }}
      >
        A curated starting list of federal and WA government grants relevant
        to Warren-Blackwood residents, businesses, farmers and community
        groups. Categories: agriculture, small business, community
        infrastructure, environment, arts &amp; culture, sport &amp; recreation.
      </p>

      <div style={callout('#fff7e6', '#f0c674')}>
        <strong style={{ color: '#7a5300' }}>Read this first — deadlines move</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.92rem' }}>
          Grant rounds open and close on different cycles. Treat every entry
          below as a <em>starting point</em>: follow the link, confirm the
          current round status, and read the eligibility criteria before
          investing time in an application. If you spot something out of date,{' '}
          <a
            href="mailto:bevan.eatts@mp.wa.gov.au?subject=Warren-Blackwood%20guide%20%E2%80%94%20grants%20update"
            style={link}
          >
            email the office
          </a>{' '}
          and we'll update it.
        </p>
      </div>

      <h2 style={{ ...h2, marginTop: 'var(--sp-lg)' }}>Jump to a category</h2>
      <ul
        style={{
          ...p,
          paddingLeft: 0,
          listStyle: 'none',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '0.5rem',
        }}
      >
        {CATEGORIES.map((c) => (
          <li key={c.id}>
            <a
              href={`#${c.id}`}
              style={{
                ...link,
                display: 'block',
                padding: '0.55rem 0.75rem',
                background: 'var(--cream)',
                border: '1px solid var(--rule)',
                borderRadius: 6,
                textDecoration: 'none',
                fontSize: '0.95rem',
              }}
            >
              {c.title} →
            </a>
          </li>
        ))}
      </ul>

      <h2 style={h2}>Where else to look</h2>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <a href="https://www.grants.gov.au/" style={link}>GrantConnect</a> —
          the single federal grants register. Free account; set up alerts by
          keyword.
        </li>
        <li>
          <a href="https://business.gov.au/grants-and-programs" style={link}>business.gov.au — Grants &amp; Programs</a> —
          searchable list of federal business-relevant grants.
        </li>
        <li>
          <a href="https://www.wa.gov.au/service/community-services/grants-and-subsidies" style={link}>wa.gov.au — Grants &amp; subsidies</a> —
          WA Government grants directory.
        </li>
        <li>
          <a href="https://www.swdc.wa.gov.au/funding-opportunities/" style={link}>South West Development Commission — Funding</a> —
          regionally-relevant programs.
        </li>
        <li>
          <a href="https://www.lotterywest.wa.gov.au/grants" style={link}>Lotterywest</a> —
          WA's largest single source of community-sector funding.
        </li>
      </ul>

      {CATEGORIES.map((category) => (
        <section key={category.id} id={category.id}>
          <h2 style={h2}>{category.title}</h2>
          <p style={p}>{category.blurb}</p>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'grid',
              gap: '0.75rem',
            }}
          >
            {category.grants.map((g) => (
              <li key={g.name} style={grantCard}>
                <div
                  style={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: '1.05rem',
                    color: 'var(--green)',
                    lineHeight: 1.25,
                  }}
                >
                  {g.name}
                </div>
                <div style={{ fontSize: '0.93rem', color: 'var(--ink)', lineHeight: 1.5 }}>
                  {g.purpose}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '0.45rem 0.85rem',
                    fontSize: '0.85rem',
                    color: 'var(--ink)',
                    marginTop: '0.15rem',
                  }}
                >
                  <div>
                    <div style={fieldLabel}>Who</div>
                    <div>{g.who}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Amount</div>
                    <div>{g.amount}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Deadline</div>
                    <div>{g.deadline}</div>
                  </div>
                </div>
                <a
                  href={g.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ ...link, fontSize: '0.9rem', marginTop: '0.1rem' }}
                >
                  Apply / more info →
                </a>
              </li>
            ))}
          </ul>
        </section>
      ))}

      <h2 style={h2}>How the MP's office can help</h2>
      <p style={p}>
        For federal grants requiring MP nomination (Stronger Communities,
        Volunteer Grants, Local Sporting Champions, Communities Environment
        Program when funded), nominations go through your <em>federal</em>{' '}
        federal member (Forrest for Augusta-Margaret River; O'Connor for the
        other five shires). For WA-government grants and SWDC programs, the
        state office can help with:
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>Pointing you to the right round or program for your project.</li>
        <li>Letters of support for competitive applications.</li>
        <li>Following up on stalled or unclear application processes.</li>
        <li>
          Flagging gaps where the state's grant settings are not working for
          our region — these become advocacy.
        </li>
      </ul>
      <p style={p}>
        See the <Link to="/contact" style={link}>contact page</Link> to get
        in touch.
      </p>

      <p
        style={{
          marginTop: 'var(--sp-lg)',
          fontSize: '0.78rem',
          color: 'var(--ink-light)',
          borderTop: '1px solid var(--rule)',
          paddingTop: 'var(--sp-sm)',
          lineHeight: 1.5,
        }}
      >
        Community-curated list compiled from publicly-available federal and
        WA Government grant pages. Not exhaustive and not endorsed by any
        funding body. Programs change; verify on the official page before
        applying. Last reviewed: June 2026. Spot something to add or correct?{' '}
        <a
          href="mailto:bevan.eatts@mp.wa.gov.au?subject=Warren-Blackwood%20guide%20%E2%80%94%20grants%20update"
          style={{ color: 'var(--green-mid)', fontWeight: 500 }}
        >
          Email the office
        </a>
        .
      </p>
    </main>
  );
}
